import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { Attachment, AttachmentType, FileAttachment, FilesEvent, PhotoAttachment } from 'src/app/model/file.model';
import { StoreService } from 'src/app/services/store.service';
import { DialogService } from 'src/app/services/dialog.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DeviceDetectorService } from 'ngx-device-detector';
import { PHOTO_CONSTRAINTS } from 'src/app/config/general.config';
import { map, Observable, switchMap } from 'rxjs';
import { AttachmentService } from '../../../../../services/attachment.service';
import { OfflineService } from '../../../../../services/offline.service';

@Component({
  selector: 'files-list',
  templateUrl: './files-list.component.html',
  styleUrls: ['./files-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilesListComponent),
      multi: true,
    }
  ]
})
export class FilesListComponent implements OnInit, ControlValueAccessor {
  @Input() files: Attachment[] = [];
  @Input() hintKey = 'UPLOAD_LIMIT';
  private _disableUpload = false;
  private _disableUploadInitial = false;
  @Input() set disableUpload(state: boolean) {
    this._disableUpload = this._disableUploadInitial = state;
  }
  get disableUpload(): boolean {
    return this._disableUpload || (this.maxFiles !== undefined && this.files.length >= this.maxFiles);
  }

  private _disableDelete = false;
  private _disableDeleteInitial = false;
  @Input() set disableDelete(state: boolean) {
    this._disableDelete = this._disableDeleteInitial = state;
  }
  get disableDelete(): boolean {
    return this._disableDelete;
  }

  private _disableDownload = false;
  private _disableDownloadInitial = false;
  @Input() set disableDownload(state: boolean) {
    this._disableDownload = this._disableDownloadInitial = state;
  }
  get disableDownload(): boolean {
    return this._disableDownload
  }

  @Input() offlineMode = false;
  @Input() type: AttachmentType;
  @Input() minFiles?: number;
  @Input() maxFiles?: number;
  header_: string | undefined = undefined;

  @Input() set header(header: string | undefined) {
    this.header_ = header;
  }
  get header(): string {
    if(this.header_ !== undefined){
      return this.header_;
    }
    if (this.type === 'PHOTO') {
      return 'PHOTOS';
    }

    if (this.type === 'OTHER') {
      return 'ATTACHMENTS';
    }

    return '';
  };
  @Output() uploadEvent = new EventEmitter<FilesEvent>();
  @Output() deleteFileEvent = new EventEmitter<Attachment>();
  @Output() originalSizeEvent = new EventEmitter<Attachment>();
  private onChange: (value: any) => void;
  private onTouched: () => void;

  hasCameraAccess = this.deviceService.isMobile();
  inputFiles: File[] = [];
  uploadSize = 0;
  uploadLimit = 52428800;
  originalSize?: string;
  get acceptedTypes(): string {
    if (this.type === 'PHOTO') {
      return 'image/*';
    }

    return '';
  }

  constructor(
    private _store: StoreService,
    private _dialogService: DialogService,
    private deviceService: DeviceDetectorService,
    private attachmentService: AttachmentService,
    private offlineService: OfflineService
  ) {
  }

  isFileAttachment(item: Attachment): item is FileAttachment {
    return item.type === 'OTHER';
  }

  isPhotoAttachment(item: Attachment): item is PhotoAttachment {
    return item.type === 'PHOTO';
  }

  ngOnInit(): void {
    this._assignInputFiles();
  }

  private _assignInputFiles(): void {
    if (this.offlineMode) {
      this.inputFiles = this.files.map(item => item.inputFile!);
    }
  }

  private validatePhoto(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const _store = this._store;
      const ext = file.name.toLowerCase().split(".").pop() ?? '';
      if(!PHOTO_CONSTRAINTS.TYPES_ALLOWED.includes(ext)) {
        _store.showWarningMsg("INVALID_PHOTO_FORMAT", {fileName: file.name});
        resolve(false);
        return;
      }
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onerror = () => {
          _store.showWarningMsg("INVALID_PHOTO_FORMAT", {fileName: file.name});
          resolve(false);
        }
        img.onload = function () {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          const res = (width * height) / 1_000_000;
          const isCorrectRes = res >= PHOTO_CONSTRAINTS.MIN_RES_MPX && res <= PHOTO_CONSTRAINTS.MAX_RES_MPX;
          if(!isCorrectRes){
            _store.showWarningMsg("INVALID_PHOTO_RESOLUTION", {fileName: file.name, resolution: res, minMpx: PHOTO_CONSTRAINTS.MIN_RES_MPX, maxMpx: PHOTO_CONSTRAINTS.MAX_RES_MPX});
          }
          resolve(isCorrectRes);
        };
      };
      reader.readAsDataURL(file);
    });
  }


  async handleFileUpload(event: Event, fromCamera = false) {
    const target = event.target as HTMLInputElement;
    const addedFiles = target.files as FileList;
    let hasInvalidFiles = false;
    const handleFileError = (warningMsg?: string) => {
      warningMsg && this._store.showWarningMsg(warningMsg);
      this.uploadSize = 0;
      target.value = "";
      this.inputFiles = [];

    }

    if (this.maxFiles !== undefined && this.files.length + addedFiles.length > this.maxFiles) {
      handleFileError("MAX_FILES_EXCEEDED");
      return;
    }

    for (let file of addedFiles) {
      this.inputFiles.push(file);
      this.uploadSize += file.size;
      const isValid = await this.validatePhoto(file);
      if(!isValid) {
        hasInvalidFiles = true;
      }
    }
    if(hasInvalidFiles) {
      handleFileError();
      return;
    }
    if (this.uploadSize > this.uploadLimit) {
      handleFileError("FILE_UPLOAD_LIMIT_EXCEEDED");
      return;
    }

    if (fromCamera) {
      this.saveCameraFilesToDevice(this.inputFiles);
    }

    this.emitUploadData();
  }

  private saveCameraFilesToDevice(files: File[]): void {
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        continue;
      }

      const safeName = this.buildCameraFilename(file);
      const objectUrl = URL.createObjectURL(file);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = safeName;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    }
  }

  private buildCameraFilename(file: File): string {
    const extension = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
    const now = new Date();
    const pad = (value: number) => value.toString().padStart(2, '0');
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `documents_photos_${timestamp}.${extension}`;
  }

  emitUploadData(): void {

    if(!this.inputFiles){
      return;
    }
    const data = new FormData();

    for (let file of this.inputFiles) {
      data.append('files', file, file.name);
    }

    const type = this.type;
    this.inputFiles = [];
    this.uploadEvent.emit({ data, type });
  }


  download(file: Attachment) {
    this._store.downloadFile(file.id!, file.originalFilename);
  }

  delete(file: Attachment) {
    this._dialogService.prompt().afterClosed().subscribe(result => {
      if (result) {
        this.inputFiles = this.inputFiles.filter(item => item.name !== file.originalFilename);
        this.files = this.files.filter(item => item.id !== file.id);
        this.deleteFileEvent.emit(file);
        this.onChange(this.files);
      }
    })
  }

  writeValue(value: Attachment[]): void {
    this.files = value || [];
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.onChange(this.files);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if(isDisabled){
      this._disableUpload = true;
      this._disableDelete = true;
      this.disableDownload = true;
    }
    else {
      this._disableUpload = this._disableUploadInitial;
      this._disableDelete = this._disableDeleteInitial;
      this._disableDownload = this._disableDownloadInitial;
    }
  }

  closePreview(): void {
    this.originalSize = undefined;
  };

  getOriginalSize(event: PhotoAttachment) {
    if (!event.id) return;

    const originalData$: Observable<string> = event.id > 0
      ? this.attachmentService.getOriginalBase(event.id).pipe(
        map(data => 'data:image/jpeg;base64,' + data.content)
      )
      : this.offlineService.getAttachmentFile(-event.id).pipe(
        switchMap(data => this.convertFileToDataUrl(data))
      );

    originalData$.subscribe(data => {

      this.originalSize = data;
    });
  }

  private convertFileToDataUrl(file: Blob): Observable<string> {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.onload = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = (error) => observer.error(error);
      reader.readAsDataURL(file);
    });
  }

  openNewWindow() {
    const base64Image = this.originalSize
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`<img src="${base64Image}" style="display:block; margin:auto;">`);
      newWindow.document.title = "Podgląd zdjęcia";
      newWindow.document.body.style.margin = '0';
      newWindow.document.body.style.padding = '0';
    }
  }
}
